import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import { messageService } from '../../services/messageService';
import { supabase } from '../../lib/supabase';

import ConversationList from './components/ConversationList';
import ChatArea from './components/ChatArea';


const MessagingCenter = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasCompletedBooking, setHasCompletedBooking] = useState(false);
  const [error, setError] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchConversations();
      checkCompletedBookings();
    }
  }, [isAuthenticated, user]);

  // Real-time subscription for messages
  useEffect(() => {
    if (!user?.id) return;

    const channel = messageService?.subscribeToMessages(user?.id, async (payload) => {
      if (payload?.eventType === 'INSERT') {
        // Add new message to the list if it's for the selected conversation
        if (selectedConversation && payload?.new?.booking_id === selectedConversation?.id) {
          const formattedMessage = {
            id: payload?.new?.id,
            senderId: payload?.new?.sender_id === user?.id ? 'me' : payload?.new?.sender_id,
            senderName: payload?.new?.sender_id === user?.id ? 'You' : selectedConversation?.participantName,
            content: payload?.new?.content,
            timestamp: payload?.new?.created_at,
            status: payload?.new?.is_read ? 'read' : 'delivered'
          };
          setMessages((prev) => [...prev, formattedMessage]);
        }
        // Refresh conversation list to show latest message
        await fetchConversations();
      } else if (payload?.eventType === 'UPDATE') {
        // Update message (e.g., read status)
        setMessages((prev) =>
          prev?.map((msg) => {
            if (msg?.id === payload?.new?.id) {
              return {
                ...msg,
                status: payload?.new?.is_read ? 'read' : msg?.status
              };
            }
            return msg;
          })
        );
      }
    });

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id, selectedConversation]);

  const checkCompletedBookings = async () => {
    try {
      // Check if user has bookings as renter OR as owner
      const { data, error: bookingError } = await supabase
        ?.from('bookings')
        ?.select('id, status, payment_status, renter_id, owner_id')
        ?.or(`renter_id.eq.${user?.id},owner_id.eq.${user?.id}`)
        ?.in('status', ['confirmed', 'completed'])
        ?.eq('payment_status', 'succeeded')
        ?.limit(1);

      if (bookingError) {
        console.log('Error checking bookings:', bookingError?.message);
        setHasCompletedBooking(false);
        return;
      }

      setHasCompletedBooking(data && data?.length > 0);
    } catch (err) {
      console.error('Failed to check completed bookings:', err);
      setHasCompletedBooking(false);
    }
  };

  const fetchConversations = async () => {
    setLoadingConversations(true);
    setError(null);

    try {
      const data = await messageService?.getConversations(user?.id);
      setConversations(data || []);
      
      // Calculate total unread
      const unreadTotal = data?.reduce((sum, conv) => sum + (conv?.unreadCount || 0), 0);
      setTotalUnread(unreadTotal);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations. Please try again.');
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchMessagesForConversation = async (conversationId, conversation) => {
    setLoadingMessages(true);

    try {
      const messagesData = await messageService?.getMessages(conversationId);

      const formattedMessages = messagesData?.map((msg) => ({
        id: msg?.id,
        senderId: msg?.senderId === user?.id ? 'me' : msg?.senderId,
        senderName: msg?.senderId === user?.id ? 'You' : msg?.sender?.fullName || conversation?.participantName,
        content: msg?.content,
        timestamp: msg?.createdAt,
        status: msg?.isRead ? 'read' : 'delivered'
      })) || [];

      setMessages(formattedMessages);

      // Mark messages as read
      const unreadMessageIds = messagesData
        ?.filter(m => m?.receiverId === user?.id && !m?.isRead)
        ?.map(m => m?.id);
      
      if (unreadMessageIds?.length > 0) {
        await messageService?.markAsRead(unreadMessageIds);
        // Refresh conversations to update unread count
        await fetchConversations();
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage?.trim() || !selectedConversation) return;

    const messageContent = newMessage?.trim();
    setNewMessage('');

    // Optimistically add message to UI immediately
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      senderId: 'me',
      senderName: 'You',
      content: messageContent,
      timestamp: new Date()?.toISOString(),
      status: 'sent'
    };
    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const sent = await messageService?.sendMessage({
        bookingId: selectedConversation?.id,
        receiverId: selectedConversation?.participantId,
        content: messageContent
      });

      // Replace optimistic message with real one
      if (sent) {
        setMessages((prev) =>
          prev?.map((msg) =>
            msg?.id === optimisticMessage?.id
              ? {
                  id: sent?.id,
                  senderId: 'me',
                  senderName: 'You',
                  content: sent?.content,
                  timestamp: sent?.createdAt,
                  status: 'delivered'
                }
              : msg
          )
        );
      }

      // Refresh conversation list to update last message
      await fetchConversations();
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
      // Remove optimistic message on error and restore input
      setMessages((prev) => prev?.filter((msg) => msg?.id !== optimisticMessage?.id));
      setNewMessage(messageContent);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setShowMobileList(false);
    await fetchMessagesForConversation(conversation?.id, conversation);
  };

  const handleBackToList = () => {
    setShowMobileList(true);
    setSelectedConversation(null);
  };

  const filteredConversations = conversations?.filter((conv) =>
    conv?.participantName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    conv?.trailerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    conv?.bookingReference?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (loading || loadingConversations) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-body text-muted-foreground">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[calc(100vh-8rem)]">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MessageSquare" size={24} className="text-primary" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="font-heading text-3xl font-bold text-foreground">Messages</h1>
                  <p className="font-body text-muted-foreground">Chat with owners and renters</p>
                </div>
              </div>
              {totalUnread > 0 && (
                <div className="flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-lg">
                  <Icon name="Bell" size={18} className="text-primary" strokeWidth={2} />
                  <span className="font-body font-semibold text-primary">{totalUnread} unread</span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-error/10 border border-error rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-body font-semibold text-error mb-1">Error</p>
                <p className="font-body text-sm text-foreground">{error}</p>
              </div>
            </div>
          )}

          {!hasCompletedBooking && (
            <div className="mb-6 bg-warning/10 border border-warning rounded-lg p-4 flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-body font-semibold text-warning mb-1">No Active Bookings</p>
                <p className="font-body text-sm text-foreground">
                  You need a confirmed booking to start messaging. Browse available trailers and make a booking to connect with owners.
                </p>
              </div>
            </div>
          )}

          {/* Messaging Interface */}
          <div className="bg-card rounded-lg border border-border overflow-hidden h-[calc(100%-8rem)] flex">
            {/* Conversation List - Desktop always visible, Mobile conditional */}
            <div className={`w-full lg:w-1/3 border-r border-border ${
              showMobileList ? 'block' : 'hidden lg:block'
            }`}>
              <ConversationList
                conversations={filteredConversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                totalUnread={totalUnread}
              />
            </div>

            {/* Chat Area - Desktop always visible, Mobile conditional */}
            <div className={`w-full lg:w-2/3 ${
              showMobileList ? 'hidden lg:flex' : 'flex'
            } flex-col`}>
              {selectedConversation ? (
                <>
                  <ChatArea
                    conversation={selectedConversation}
                    messages={messages}
                    isTyping={isTyping}
                    onBack={handleBackToList}
                    loading={loadingMessages}
                  />

                  {/* Message Input */}
                  <div className="p-4 border-t border-border bg-muted/30">
                    <div className="flex items-end space-x-2">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e?.target?.value)}
                        onKeyDown={(e) => {
                          if (e?.key === 'Enter' && !e?.shiftKey) {
                            e?.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Type your message..."
                        rows={2}
                        className="flex-1 px-4 py-2 bg-background border border-input rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-ring font-body text-sm"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage?.trim()}
                        className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-body font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 h-[72px]"
                      >
                        <Icon name="Send" size={18} strokeWidth={2} />
                        <span className="hidden sm:inline">Send</span>
                      </button>
                    </div>
                    <p className="font-caption text-xs text-muted-foreground mt-2">
                      Press Enter to send, Shift + Enter for new line
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="MessageSquare" size={32} className="text-muted-foreground" strokeWidth={2} />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground mb-2">Select a Conversation</h3>
                    <p className="font-body text-muted-foreground">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagingCenter;